package ui.app.assign.detail.kindergarten

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.staggeredgrid.LazyStaggeredGridScope
import androidx.compose.foundation.lazy.staggeredgrid.LazyVerticalStaggeredGrid
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridCells
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridItemSpan
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import apps.App
import common.frontend.api.Io
import compose.assets.Svg
import compose.assets.svg.Chevron_right
import compose.assets.svg.Music_note
import org.jetbrains.compose.resources.StringResource
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.navTo_DevicePreviewUI
import tk.mallumo.compose.navigation.navTo_LocationUI
import tk.mallumo.compose.navigation.navTo_ResourceUI
import tools.rememberString
import tools.toHex
import ui.app.assign.detail.kindergarten.editor.icon
import ui.app.assign.detail.kindergarten.editor.label
import ui.device.detail.DeviceArgs
import ui.location.detail.LocationArgs
import ui.resource.detail.ResourceArgs
import ui.widget.LazyIcon
import ui.widget.LazyImage
import ui.widget.Space
import ui.widget.TwoFieldText

@Composable
fun AppKindergartenScope.KindergartenSummary(
    data: Io.AppDetail,
    clickable: Boolean,
    footer: LazyStaggeredGridScope.() -> Unit = {}
) {
    val content = remember {
        (data.data as App.Kindergarten).content
    }
    LazyVerticalStaggeredGrid(
        columns = StaggeredGridCells.FixedSize(512.dp),
        contentPadding = PaddingValues(0.dp, 16.dp, 0.dp, 64.dp),
        horizontalArrangement = Arrangement.SpaceEvenly,
        verticalItemSpacing = 16.dp,
        modifier = Modifier.fillMaxSize()
    ) {
        item {
            BasicInfo(clickable, data, content)
        }
        item {
            Colors(clickable, content)
        }
        item {
            Background(clickable, content)
        }
        item {
            Devices(clickable, data)
        }
        if (content.imagesCustom.wireframe.isNotEmpty()) {
            item(span = StaggeredGridItemSpan.FullLine) {
                Wireframes(clickable, content)
            }
        }

        if (content.imagesCustom.photoLarge.isNotEmpty()) {
            item(span = StaggeredGridItemSpan.FullLine) {
                Pictures(clickable, content)
            }
        }

        if (content.imagesCustom.photoSmall.isNotEmpty()) {
            item(span = StaggeredGridItemSpan.FullLine) {
                Icons(clickable, content)
            }
        }
        if (content.audio.isNotEmpty()) {
            item(span = StaggeredGridItemSpan.FullLine) {
                Audio(clickable, content)
            }
        }
        footer()
    }
}

@Composable
private fun AppKindergartenScope.BasicInfo(clickable: Boolean, data: Io.AppDetail, content: App.Kindergarten.Content) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainer
        )
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(8.dp),
            modifier = Modifier.fillMaxWidth().padding(16.dp),
        ) {
            Text(
                text = data.name,
                style = MaterialTheme.typography.headlineLarge
            )
            HorizontalDivider()
            TwoFieldText(
                prefix = {
                    LazyImage(
                        id = App.Kindergarten.AudienceType[content.audienceType].icon(),
                        alphaBackground = false,
                        modifier = Modifier.size(24.dp)
                    )
                },
                label = Res.string.layer_bottom__kindergarten_audience_title.rememberString(),
                message = App.Kindergarten.AudienceType[content.audienceType].label().rememberString()
            )
            TwoFieldText(
                prefix = {
                    LazyImage(
                        id = App.Kindergarten.ThemeType[content.themeType].icon(),
                        alphaBackground = false,
                        modifier = Modifier.size(24.dp)
                    )
                },
                label = Res.string.layer_bottom__kindergarten_theme_title.rememberString(),
                message = App.Kindergarten.ThemeType[content.themeType].label().rememberString()
            )
            HorizontalDivider()
            LazyImage(
                id = data.logo,
                contentScale = ContentScale.Crop,
                modifier = Modifier.size(256.dp)
                    .clip(CardDefaults.shape)
                    .clickable(clickable && data.logo > 0) {
                        nav.navTo_ResourceUI(
                            ResourceArgs(
                                companyId = args.companyId,
                                resourceId = data.logo
                            )
                        )
                    }
            )

        }
    }
}


@Composable
private fun AppKindergartenScope.Colors(clickable: Boolean, content: App.Kindergarten.Content) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainer
        )
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(8.dp),
            modifier = Modifier.fillMaxWidth()
                .padding(bottom = 16.dp)
        ) {
            CardHeader(
                icon = 54590300,
                label = Res.string.layer_bottom__kindergarten_colors_title,
                modifier = Modifier.padding(16.dp),
            )

            if (display.isMobile) {
                Row(Modifier.padding(horizontal = 16.dp)) {
                    Text(
                        Res.string.layer_bottom__kindergarten_colors_surface.rememberString(),
                        modifier = Modifier.weight(1F).align(Alignment.CenterVertically)
                    )
                    Spacer(
                        Modifier.size(24.dp)
                            .background(Color.White)
                            .padding(1.dp)
                            .background(Color(content.colors.surface))
                    )
                }
                Row(Modifier.padding(horizontal = 16.dp)) {
                    Text(
                        Res.string.layer_bottom__kindergarten_colors_on_surface.rememberString(),
                        modifier = Modifier.weight(1F).align(Alignment.CenterVertically)
                    )
                    Spacer(
                        Modifier.size(24.dp)
                            .background(Color.White)
                            .padding(1.dp)
                            .background(Color(content.colors.onSurface))
                    )
                }
            } else {
                TwoFieldText(
                    postfix = {
                        Spacer(
                            Modifier.size(24.dp)
                                .background(Color.White)
                                .padding(1.dp)
                                .background(Color(content.colors.surface))
                        )
                    },
                    label = Res.string.layer_bottom__kindergarten_colors_surface.rememberString(),
                    message = Color(content.colors.surface).toHex(),
                    modifier = Modifier.padding(horizontal = 16.dp)
                )
                TwoFieldText(
                    postfix = {
                        Spacer(
                            Modifier.size(24.dp)
                                .background(Color.White)
                                .padding(1.dp)
                                .background(Color(content.colors.onSurface))
                        )
                    },
                    label = Res.string.layer_bottom__kindergarten_colors_on_surface.rememberString(),
                    message = Color(content.colors.onSurface).toHex(),
                    modifier = Modifier.padding(horizontal = 16.dp)
                )
            }
        }
    }
}


@Composable
private fun AppKindergartenScope.Background(clickable: Boolean, content: App.Kindergarten.Content) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainer
        )
    ) {
        Column(
            modifier = Modifier.fillMaxWidth()
                .padding(),
        ) {

            CardHeader(
                icon = 103638300,
                label = Res.string.layer_bottom__kindergarten_background_title,
                modifier = Modifier.padding(16.dp)
            )
            LazyImage(
                id = content.backgroundImg,
                alphaBackground = false,
                contentScale = ContentScale.Crop,
                modifier = Modifier.fillMaxWidth()
                    .aspectRatio(16F / 9F)
                    .clip(RoundedCornerShape(0.dp, 0.dp, 8.dp, 8.dp))
                    .clickable(clickable && content.backgroundImg > 0) {
                        nav.navTo_ResourceUI(
                            ResourceArgs(
                                companyId = args.companyId,
                                resourceId = content.backgroundImg
                            )
                        )
                    }
            )
        }
    }
}


@Composable
private fun AppKindergartenScope.Devices(clickable: Boolean, data: Io.AppDetail) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainer
        )
    ) {

        CardHeader(
            icon = 8087300,
            label = Res.string.layer_bottom_detail_editor__page_devices,
            modifier = Modifier.padding(horizontal = 16.dp)
                .padding(top = 16.dp, bottom = 8.dp)
        )

        @Composable
        fun DeviceBody(device: Io.AppDetail.DeviceItem) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(4.dp),
                modifier = Modifier.padding(8.dp)
            ) {
                Text(
                    device.name,
                    modifier = Modifier.weight(1F)
                )
                if (clickable) Icon(Svg.Chevron_right, null, modifier = Modifier.align(Alignment.CenterVertically))
            }
        }
        Column(
            verticalArrangement = Arrangement.spacedBy(
                if (clickable) 0.dp
                else 8.dp
            ),
            modifier = Modifier.fillMaxWidth()
                .padding(horizontal = 16.dp),
        ) {

            data.locations
                .filter { it.devices.any { it.enabled } }
                .also {
                    if (it.isEmpty()) Text("No attached devices")
                }
                .forEachIndexed { index, location ->
                    if (index == 0) Space(4.dp)
                    else {
                        Space(8.dp)
                        HorizontalDivider()
                        Space(2.dp)
                        HorizontalDivider()
                        Space(8.dp)
                    }

                    Row(
                        Modifier.fillMaxWidth()
                            .clip(CardDefaults.shape)
                            .clickable(clickable) {
                                nav.navTo_LocationUI(
                                    LocationArgs(
                                        companyId = args.companyId,
                                        locationId = location.id
                                    )
                                )
                            }
                            .padding(4.dp)
                    ) {
                        Canvas(Modifier.size(6.dp).align(Alignment.CenterVertically)) {
                            drawCircle(Color.White, size.width / 2F)
                        }
                        Text(
                            text = location.name,
                            style = MaterialTheme.typography.labelSmall,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                            modifier = Modifier.fillMaxWidth().padding(start = 4.dp)
                        )
                    }
                    location.devices
                        .filter { it.enabled }
                        .forEach { device ->
                            if (clickable) {
                                Card(
                                    onClick = {
                                        nav.navTo_DevicePreviewUI(
                                            DeviceArgs(
                                                deviceId = device.id
                                            )
                                        )
                                    }) {
                                    DeviceBody(device)
                                }
                            } else {
                                Card { DeviceBody(device) }
                            }
                        }
                }
        }
        Space(16.dp)
    }
}

@Composable
private fun AppKindergartenScope.Wireframes(clickable: Boolean, content: App.Kindergarten.Content) {
    Column {
        Text(
            text = Res.string.layer_bottom_detail_editor__page_wireframe.rememberString(),
            style = MaterialTheme.typography.labelMedium,
            modifier = Modifier.padding(horizontal = 8.dp)
        )
        HorizontalDivider(Modifier.fillMaxWidth())
        FlowRow(
            Modifier.fillMaxWidth()
                .wrapContentHeight(Alignment.Top)
                .padding(16.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.Start),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            content.imagesCustom.wireframe.forEach {
                Card(
                    modifier =
                        if (display.isMobile) Modifier.fillMaxWidth()
                        else Modifier.width(232.dp),
                    colors = CardDefaults.cardColors(
                        containerColor = MaterialTheme.colorScheme.surfaceContainer
                    )
                ) {
                    LazyImage(
                        id = it.resource,
                        alphaBackground = false,
                        contentScale = ContentScale.Crop,
                        modifier = Modifier.fillMaxWidth()
                            .aspectRatio(16F / 9F)
                            .clip(RoundedCornerShape(8.dp, 8.dp, 0.dp, 0.dp))
                            .clickable(clickable) {
                                nav.navTo_ResourceUI(
                                    ResourceArgs(
                                        companyId = args.companyId,
                                        resourceId = it.resource
                                    )
                                )
                            }
                    )
                    it.names.firstOrNull()?.also {
                        Text(
                            text = it,
                            overflow = TextOverflow.Ellipsis,
                            maxLines = 1,
                            modifier = Modifier.fillMaxWidth()
                                .padding(4.dp)
                        )
                    }
                }
            }
        }
    }
}


@Composable
private fun AppKindergartenScope.Pictures(clickable: Boolean, content: App.Kindergarten.Content) {
    Column {
        Text(
            text = Res.string.layer_bottom_detail_editor__page_pictures.rememberString(),
            style = MaterialTheme.typography.labelMedium,
            modifier = Modifier.padding(horizontal = 8.dp)
        )
        HorizontalDivider(Modifier.fillMaxWidth())
        FlowRow(
            Modifier.fillMaxWidth()
                .wrapContentHeight(Alignment.Top)
                .padding(16.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.Start),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            content.imagesCustom.photoLarge.forEach {
                Card(
                    modifier =
                        if (display.isMobile) Modifier.fillMaxWidth()
                        else Modifier.width(232.dp),
                    colors = CardDefaults.cardColors(
                        containerColor = MaterialTheme.colorScheme.surfaceContainer
                    )
                ) {
                    LazyImage(
                        id = it.resource,
                        alphaBackground = false,
                        contentScale = ContentScale.Crop,
                        modifier = Modifier.fillMaxWidth()
                            .aspectRatio(16F / 9F)
                            .clip(RoundedCornerShape(8.dp, 8.dp, 0.dp, 0.dp))
                            .clickable(clickable) {
                                nav.navTo_ResourceUI(
                                    ResourceArgs(
                                        companyId = args.companyId,
                                        resourceId = it.resource
                                    )
                                )
                            }
                    )
                    it.names.forEach {
                        Row(
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            modifier = Modifier.padding(horizontal = 8.dp)
                        ) {
                            Canvas(Modifier.size(6.dp).align(Alignment.CenterVertically)) {
                                drawCircle(Color.White, size.width / 2F)
                            }
                            Text(
                                text = it,
                                overflow = TextOverflow.Ellipsis,
                                maxLines = 1,
                                modifier = Modifier.fillMaxWidth()
                                    .padding(4.dp)
                            )
                        }
                    }
                }
            }
        }
    }
}


@Composable
private fun AppKindergartenScope.Icons(clickable: Boolean, content: App.Kindergarten.Content) {
    Column {
        Text(
            text = Res.string.layer_bottom_detail_editor__page_icons.rememberString(),
            style = MaterialTheme.typography.labelMedium,
            modifier = Modifier.padding(horizontal = 8.dp)
        )
        HorizontalDivider(Modifier.fillMaxWidth())

        @Composable
        fun Body(item: App.Kindergarten.Photo) {
            LazyImage(
                id = item.resource,
                alphaBackground = false,
                contentScale = ContentScale.Crop,
                modifier = Modifier.fillMaxSize()
            )
        }
        FlowRow(
            Modifier.fillMaxWidth()
                .wrapContentHeight(Alignment.Top)
                .padding(16.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.Start),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            content.imagesCustom.photoSmall.forEach {
                if (clickable) {
                    Card(
                        onClick = {
                            nav.navTo_ResourceUI(
                                ResourceArgs(
                                    companyId = args.companyId,
                                    resourceId = it.resource
                                )
                            )
                        },
                        modifier = Modifier.size(64.dp),
                        colors = CardDefaults.cardColors(
                            containerColor = Color(it.colorBg)
                        )
                    ) {
                        Body(it)
                    }
                } else {
                    Card(
                        modifier = Modifier.size(64.dp),
                        colors = CardDefaults.cardColors(
                            containerColor = Color(it.colorBg)
                        )
                    ) {
                        Body(it)
                    }
                }
            }
        }
    }
}


@Composable
private fun AppKindergartenScope.Audio(clickable: Boolean, content: App.Kindergarten.Content) {
    Column {
        Text(
            text = Res.string.layer_bottom_detail_editor__page_audio.rememberString(),
            style = MaterialTheme.typography.labelMedium,
            modifier = Modifier.padding(horizontal = 8.dp)
        )
        HorizontalDivider(Modifier.fillMaxWidth())

        @Composable
        fun Body(audio: App.Kindergarten.Audio) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(4.dp),
                modifier = Modifier.padding(8.dp)
            ) {
                Icon(Svg.Music_note, null)
                Text(text = audio.name)
                if (clickable) Icon(Svg.Chevron_right, null, modifier = Modifier.align(Alignment.CenterVertically))
            }
        }
        FlowRow(
            Modifier.fillMaxWidth()
                .wrapContentHeight(Alignment.Top)
                .padding(16.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.Start),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            content.audio.forEach {
                if (clickable) {
                    Card(
                        onClick = {
                            nav.navTo_ResourceUI(
                                ResourceArgs(
                                    companyId = args.companyId,
                                    resourceId = it.resource
                                )
                            )
                        },
                        colors = CardDefaults.cardColors(
                            containerColor = MaterialTheme.colorScheme.surfaceContainer
                        )
                    ) {
                        Body(it)
                    }
                } else {
                    Card(
                        colors = CardDefaults.cardColors(
                            containerColor = MaterialTheme.colorScheme.surfaceContainer
                        )
                    ) {
                        Body(it)
                    }
                }
            }
        }
    }
}

@Composable
private fun CardHeader(icon: Long, label: StringResource, modifier: Modifier = Modifier) {
    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = modifier) {
        LazyIcon(icon, Modifier.size(24.dp))
        Text(
            text = label.rememberString(),
            fontWeight = FontWeight.Light
        )
    }
}